import BaseSubscriptionModal from 'app/components/subscriptionModals/BaseSubscriptionModal';
import { Button, Icon } from '@kontentino/ui';
import { faLockKeyhole } from '@fortawesome/pro-regular-svg-icons/faLockKeyhole';
import { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { ModalManager } from 'context/ModalRouter';
import { Module } from 'config';
import IntercomUtils from 'app/utils/intercom';
import { useOpenBillingPage } from 'app/hooks/useBillingUrl';

/**
 * The same destination BlockedAccessBanner's `<support>` link points at, read
 * from the shared config instead of re-typed — the banner and this modal are
 * the two halves of the contradiction R2-N3 reported, so a divergent support
 * URL would be the same defect in a smaller form. Asserted equal in
 * AccountOnHold.test.tsx.
 */
const SUPPORT_URL = Module.OldApp.URL.HELP_CENTER.RESOURCE_CENTER;

type Props = {
  open?: boolean;
  modalManager?: ModalManager;
};

/**
 * The app-wide blocked modal for an admin hold (`blockedReason === 'on_hold'`).
 *
 * Round-2 N3. Every blocked account got SubscriptionExpired — "Your
 * subscription has expired" over a "Choose your plan" CTA — including the ones
 * blocked by an operator hold, for which buying a plan restores nothing:
 * `EntitlementsService::statusFor()` returns ON_HOLD straight off
 * `$subscription->isHeld()`, before any status or period is considered, and
 * only an operator clearing `held_at` reverses it. Meanwhile the billing page
 * one click away already said exactly that, via BlockedAccessBanner. A held
 * customer was being sold a plan by one surface and told it wouldn't help by
 * the other.
 *
 * So this modal renders the banner's own `billing.blockedAccess.onHold.*`
 * strings rather than near-duplicates of them: the two surfaces cannot drift
 * apart in wording because there is only one wording, already translated into
 * all 26 locales. Only the CTA label is new (`onHold.cta`), and it repeats the
 * link text the subtitle already carries in each locale.
 *
 * No plan picker, no benefits list, no "cancel your plan" footer — none of the
 * three is an action that ends the hold, and offering them is what made the old
 * modal wrong.
 */
const AccountOnHold: FC<Props> = ({ open, modalManager }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const openBillingPage = useOpenBillingPage();

  /**
   * The escape hatch. This modal is a hard block (no esc, no overlay-close, no
   * X — see ModalDialog), and the billing page is the ONE route where
   * SubscriptionModals suppresses it (the BUG-39 guard). Removing the old plan
   * CTA also removed the only in-app path there, which would strand a held
   * customer with no way to reach their invoices, manage a card, or read the
   * BlockedAccessBanner written for exactly this state. So a secondary,
   * non-primary link to billing is kept — labelled "view billing", not a plan
   * pitch — using the same in-app navigation SubscriptionExpiredButtons uses so
   * it does not bounce back out of the SPA basename (also BUG-39).
   */
  const handleViewBilling = () => openBillingPage('billing', { navigate });

  /**
   * Open the messenger when Intercom is actually on the page, otherwise send
   * the customer to the help centre. The fallback is the point: this is the
   * only action the modal offers, and `IntercomUtils` silently no-ops whenever
   * the widget is unavailable (no app id, dev mode, script blocked) — a primary
   * CTA that did nothing in those cases would leave a blocked customer with no
   * route out at all.
   */
  const handleContactSupport = () => {
    if (IntercomUtils.isAvailable()) {
      IntercomUtils.newConversation(t('billing.blockedAccess.onHold.heading'));
      return;
    }

    window.open(SUPPORT_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <BaseSubscriptionModal
      open={open}
      modalManager={modalManager}
      image={
        <div
          className="tw-flex tw-h-16 tw-w-16 tw-items-center tw-justify-center tw-rounded-full tw-bg-warning-10 tw-text-2xl tw-text-warning-100"
          aria-hidden
          data-cy="subscription-on-hold_icon"
        >
          <Icon icon={faLockKeyhole} />
        </div>
      }
      title={
        <div
          className="tw-text-center tw-text-xl tw-font-medium"
          data-cy="subscription-on-hold_title"
        >
          {t('billing.blockedAccess.onHold.heading')}
        </div>
      }
      description={
        <span data-cy="subscription-on-hold_description">
          <Trans
            i18nKey="billing.blockedAccess.onHold.subtitle"
            components={{
              support: (
                /* Trans supplies the link text from the translated copy. */
                // eslint-disable-next-line jsx-a11y/anchor-has-content
                <a
                  href={SUPPORT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tw-cursor-pointer tw-text-grayscale-100 tw-underline"
                  data-cy="subscription-on-hold_support-link"
                />
              ),
            }}
          />
        </span>
      }
      buttons={
        <div className="tw-flex tw-w-full tw-flex-col tw-items-center tw-gap-3">
          <Button
            onClick={handleContactSupport}
            className="tw-w-full"
            data-cy="subscription-on-hold_contact-support-button"
          >
            {t('billing.blockedAccess.onHold.cta')}
          </Button>
          <button
            type="button"
            onClick={handleViewBilling}
            className="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-grayscale-100 tw-underline"
            data-cy="subscription-on-hold_view-billing-link"
          >
            {t('billing.blockedAccess.onHold.viewBilling')}
          </button>
        </div>
      }
    />
  );
};

export default AccountOnHold;
